import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

const TOOL_NAME_ACRONYMS = new Set(["API", "GAQL", "ID", "MCP", "URL"]);

function formatToolWord(word: string): string {
	const upperWord = word.toUpperCase();
	if (TOOL_NAME_ACRONYMS.has(upperWord)) {
		return upperWord;
	}
	if (/^\d+$/.test(word)) {
		return word;
	}
	const lowerWord = word.toLowerCase();
	return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
}

/**
 * Format tool names for display
 * Examples:
 * - "mcp__1__gaql_query" → "GAQL Query"
 * - "mcp__context7__get-library-docs" → "Get Library Docs"
 * - "simple_tool" → "Simple Tool"
 */
export function formatToolName(toolName: string): string {
	// Remove mcp__ prefix and numeric prefixes
	let formatted = toolName.replace(/^mcp__\d+__/i, "").replace(/^mcp__/i, "");

	// Split mixed naming styles into individual words
	formatted = formatted
		.replace(/[_-]+/g, " ")
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
		.replace(/\s+/g, " ")
		.trim();

	// Convert to title case with acronym preservation
	formatted = formatted.split(" ").filter(Boolean).map(formatToolWord).join(" ");

	return formatted;
}

const TITLE_CASE_FIXED_WORDS = new Map<string, string>([
	["ai", "AI"],
	["seo", "SEO"],
	["ppc", "PPC"],
	["roi", "ROI"],
	["faq", "FAQ"],
	["api", "API"],
	["oauth", "OAuth"],
	["kpi", "KPI"],
	["kpis", "KPIs"],
	["google", "Google"],
	["ahrefs", "Ahrefs"],
	["brevo", "Brevo"],
	["activecampaign", "ActiveCampaign"],
	["adrapid", "Adrapid"],
	["canva", "Canva"],
	["klaviyo", "Klaviyo"],
	["mailchimp", "Mailchimp"],
	["m8tes", "m8tes"],
]);

function formatSentenceCaseWord(word: string, isFirstWord: boolean): string {
	const lowerWord = word.toLowerCase();
	const fixedWord = TITLE_CASE_FIXED_WORDS.get(lowerWord);
	if (fixedWord) {
		return fixedWord;
	}

	if (isFirstWord) {
		return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
	}

	return lowerWord;
}

/**
 * Convert title-like strings to sentence case while preserving key acronyms and product names.
 * Examples:
 * - "AI Teammates That Actually Do the Work" -> "AI teammates that actually do the work"
 * - "Google Ads Playbook" -> "Google Ads playbook"
 */
export function formatSentenceCaseTitle(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) {
		return value;
	}

	let isFirstWord = true;

	const sentenceCased = trimmed
		.split(/\s+/)
		.map((token) =>
			token.replace(/[A-Za-z][A-Za-z0-9'-]*/g, (word) => {
				const formattedWord = formatSentenceCaseWord(word, isFirstWord);
				isFirstWord = false;
				return formattedWord;
			})
		)
		.join(" ");

	return sentenceCased
		.replace(/\bgoogle ads\b/gi, "Google Ads")
		.replace(/\bsearch console\b/gi, "Search Console")
		.replace(/\bkeyword planner\b/gi, "Keyword Planner");
}
